import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
	filterByConfidence,
	hasConfidence,
	indexTracks,
	lineCrossings,
	lineFromAnalysis,
	minSensitivity,
	sizeHistogram,
	sizesUpTo,
	snapToGrid,
} from './tracksModel';
import { bodySizes } from './flightModel';
import { applyCorrections, correctionsSummary } from './corrections';
import { anyOne, singular } from './subjectWords';
import { countByKind, kindsOf } from './kinds';

// a size filter starts at the usual size divided and multiplied by this (Find's SIZE_SPAN)
export const SIZE_SPAN = 2.5;

/**
 * The tracks a size range keeps: in range, of unknown size, or added by a person (never hidden by size). `range` is
 * one [min, max] for every track, or a function of (track, its index) giving its range, null keeping it.
 */
export const keptBySize = (tracks, values, range) => {
	if (!range) return tracks;
	const rangeOf = typeof range === 'function' ? range : () => range;
	return tracks.filter((tr, k) => {
		const r = rangeOf(tr, k);
		const v = values[k];
		return !r || tr.added || !Number.isFinite(v) || (v >= r[0] && v <= r[1]);
	});
};

/**
 * Where a size filter starts: the usual sizes ÷ and × SIZE_SPAN in metres; null (every size) otherwise. The usual size
 * is a length; a track seen with the camera tilted is measured across the image (bodySizes `tilted`), at most its
 * length but as little as its width, so for `tilted` only the upper limit holds.
 */
export const autoSizeRange = (usual, unit, tilted = false) =>
	unit === 'm' && usual?.length
		? [tilted ? 0 : Math.min(...usual) / SIZE_SPAN, Math.max(...usual) * SIZE_SPAN]
		: null;

/** The count modes, as the page and the report name them. */
export const MODE_LABEL = { hover: 'Total count', line: 'Line crossing', max: 'Most in view' };

/** A person's choice to keep the usual size (the automatic range, by kind and tilt), as opposed to a range set. */
export const USUAL = 'usual';

/** A track's own range: an automatic one keeps only its upper limit for a track measured across the image. */
export const trackRange = (range, auto, tilted) =>
	range && auto && tilted ? [0, range[1]] : range;

/**
 * Everything the count panel shows, computed in the browser from tracks.json: the count for the chosen mode at the
 * current frame, its total, the series over time, in-view numbers and the size histogram. Every total comes from the
 * tracks, never from the job's stored statistics (people do not know about those), so the page and the PDF report
 * made from it always show the same number.
 */

// models that output a track once it has been tracked for 1 s (backend TRACKER_MODELS)
export const TRACKER_MODELS = ['BFM', 'RFDETR', 'BFM-RFDETR', 'SEARCH'];
export const isTrackerModel = modelId =>
	TRACKER_MODELS.includes(String(modelId || '').toUpperCase());

const sameLine = (a, b) =>
	a &&
	b &&
	a.start[0] === b.start[0] &&
	a.start[1] === b.start[1] &&
	a.end[0] === b.end[0] &&
	a.end[1] === b.end[1];

// flight: indexFlight(telemetry.json) when the video has flight data; body sizes are then in metres, else pixels
// layer: the corrections layer (people's edits); applied after the sensitivity filter, so what someone added stays
const useCountModel = ({
	tracksDoc,
	statistics,
	virtualLine,
	flight = null,
	layer = null,
	subject = '',
	// the usual size of each thing counted ([{ name, length_m }], from the assistant), where the size filter starts
	usualSizes = null,
	// the job's model (video_analyses.model_id): a tracker model counts a track once it has been tracked for 1 s
	modelId = null,
}) => {
	// Sensitivity: hide tracks whose best detection is below this confidence (never below the job's threshold)
	const floor = minSensitivity(tracksDoc);
	const [sensitivity, setSensitivity] = useState(floor);
	useEffect(() => setSensitivity(floor), [floor, tracksDoc]);
	const deferred = useDeferredValue(sensitivity);
	const filtered = deferred > floor + 1e-9;
	// people's edits
	const changes = useMemo(() => correctionsSummary(tracksDoc, layer), [tracksDoc, layer]);
	const baseIndex = useMemo(
		() =>
			tracksDoc
				? indexTracks(applyCorrections(filterByConfidence(tracksDoc, deferred), layer))
				: null,
		[tracksDoc, deferred, layer]
	);
	// Size: keep the tracks whose size is in a range ([min, max] in metres with flight data, else pixels; null keeps
	// all). It starts at every size; with metres the usual size ÷ and × SIZE_SPAN (as Find's size filter) is one choice
	// away. A track of unknown size, and what a person added, are always kept.
	const baseSizes = useMemo(
		() => (baseIndex ? bodySizes(baseIndex, flight) : null),
		[baseIndex, flight]
	);
	// one search for several kinds: each kind has its own usual size and range ('' is every track of a one-kind video)
	const kinds = useMemo(() => kindsOf(baseIndex?.doc), [baseIndex]);
	const groups = useMemo(() => (kinds.length ? kinds : ['']), [kinds]);
	const groupOf = useCallback(tr => (kinds.length ? tr.label || '' : ''), [kinds]);
	const usualOf = useMemo(() => {
		const known = (usualSizes || []).filter(
			u => Number.isFinite(u?.length_m) && u.length_m > 0
		);
		const out = Object.fromEntries(known.map(u => [singular(u.name), [u.length_m]]));
		out[''] = known.map(u => u.length_m);
		return out;
	}, [usualSizes]);
	// per group: whether every track of it with a size was measured across the image (the camera tilted); null for a
	// group with no track measured
	const allTilted = useMemo(() => {
		const out = Object.fromEntries(groups.map(g => [g, null]));
		(baseIndex?.tracks || []).forEach((tr, k) => {
			const g = groupOf(tr);
			if (!(g in out) || !Number.isFinite(baseSizes.values[k])) return;
			out[g] = (out[g] ?? true) && !!baseSizes.tilted[k];
		});
		return out;
	}, [baseIndex, baseSizes, groups, groupOf]);
	const unit = baseSizes?.unit;
	const autoRangeOf = useCallback(
		g => autoSizeRange(usualOf[g], unit, !!allTilted[g]),
		[unit, usualOf, allTilted]
	);
	// per group: nothing chosen is every size; USUAL the automatic range; else the range a person set. A filter is a
	// choice, so the page opens on every track (a default range once hid most of a herd on the page)
	const [chosen, setChosen] = useState({});
	useEffect(() => setChosen({}), [tracksDoc]);
	const rangeOfGroup = useCallback(
		g => (!(g in chosen) ? null : chosen[g] === USUAL ? autoRangeOf(g) : chosen[g]),
		[chosen, autoRangeOf]
	);
	const index = useMemo(() => {
		if (!baseIndex || !groups.some(g => rangeOfGroup(g))) return baseIndex;
		const kept = keptBySize(baseIndex.tracks, baseSizes.values, (tr, k) => {
			const g = groupOf(tr);
			return trackRange(rangeOfGroup(g), chosen[g] === USUAL, baseSizes.tilted[k]);
		});
		return kept.length === baseIndex.tracks.length
			? baseIndex
			: indexTracks({ ...baseIndex.doc, tracks: kept });
	}, [baseIndex, baseSizes, groups, groupOf, rangeOfGroup, chosen]);
	const sizeHidden = baseIndex && index ? baseIndex.tracks.length - index.tracks.length : 0;
	const keptIds = useMemo(() => new Set((index?.tracks || []).map(tr => tr.id)), [index]);
	const sizeGroups = useMemo(
		() =>
			baseIndex
				? groups.map(g => {
						const members = baseIndex.tracks
							.map((tr, k) => [tr, baseSizes.values[k]])
							.filter(([tr]) => groupOf(tr) === g);
						return {
							kind: g,
							values: members.map(([, v]) => v).filter(Number.isFinite),
							range: rangeOfGroup(g),
							auto: chosen[g] === USUAL,
							chosen: g in chosen,
							usualRange: autoRangeOf(g),
							usual: usualOf[g] || [],
							hidden: members.filter(([tr]) => !keptIds.has(tr.id)).length,
						};
					})
				: [],
		[baseIndex, baseSizes, groups, groupOf, rangeOfGroup, autoRangeOf, chosen, usualOf, keptIds]
	);
	const method = statistics?.counting_method_used || tracksDoc?.counting_method;
	const officialLine = useMemo(
		() => (index ? lineFromAnalysis(virtualLine, index) : null),
		[index, virtualLine]
	);
	const [mode, setMode] = useState(method === 'line_based' ? 'line' : 'hover');
	const [line, setLine] = useState(officialLine);
	const [frame, setFrame] = useState(0);

	useEffect(() => setLine(officialLine), [officialLine]);
	useEffect(() => setMode(method === 'line_based' ? 'line' : 'hover'), [method]);

	const lineStats = useMemo(
		() => (index && line ? lineCrossings(index, line) : null),
		[index, line]
	);
	const lineMoved = !sameLine(line, officialLine) || method !== 'line_based';
	const sizes = useMemo(() => (index ? bodySizes(index, flight) : null), [index, flight]);
	const hist = useMemo(
		() => ({
			...sizeHistogram(index ? sizesUpTo(index, frame, sizes) : [], sizes?.unit),
			// what the sizes are, from every track before the size filter (as the filter's)
			across: !!baseSizes?.across,
			// a kind with nothing measured says nothing about how the others were measured
			allAcross: !!baseSizes?.across && Object.values(allTilted).every(v => v !== false),
			heightRef: flight?.doc?.height_ref || null,
			flightWithoutHeight: !!flight && !flight.hasFootprint,
		}),
		[index, frame, sizes, flight, baseSizes, allTilted]
	);

	if (!index) return { index: null, setFrame };
	const last = index.nFrames - 1;
	// the player reports every frame (it draws smoothed tracks); the counts read the frame's sample
	const f = snapToGrid(index.grid, Math.min(frame, last));

	let count;
	// the page's "… in total" for the mode, as a value (the report's big number)
	let total;
	// line mode: crossing events, both directions
	let events = null;
	let subtitle;
	// note: what is true of the number right now (stays in view); explain: how the mode counts (the info button;
	// none for Most in view or a line job's own line, where the subtitle says it all, so the button is absent there)
	let note = null;
	let noteWarn = false;
	let explain = null;
	let series;
	if (mode === 'line') {
		count = lineStats?.netSeries[f] ?? 0;
		total = lineStats?.net ?? 0;
		events = lineStats?.events ?? 0;
		subtitle = `passed the line so far · ${total} in total`;
		series = lineStats?.netSeries;
		if (lineMoved && method === 'line_based') {
			note =
				'Recalculated for this line (the analysis used the line drawn before processing)';
			noteWarn = true;
		} else if (lineMoved) {
			note = 'Counted across this line in the browser';
			explain = `The analysis counted every ${anyOne(subject)}, not a line; crossings of this line are counted here from the tracks, in both directions.`;
		} else note = `${events} crossing events, both directions`;
	} else if (mode === 'max') {
		count = index.inView[f];
		total = index.maxInView;
		subtitle = `in view now · most at once ${total}`;
		series = index.inView;
	} else {
		count = index.uniqueSeries[f];
		total = index.uniqueSeries[last];
		subtitle = `counted so far · ${total} in total`;
		explain = isTrackerModel(modelId)
			? `Each ${anyOne(subject)} counts once, after it has been tracked for 1 s.`
			: `Each ${anyOne(subject)} counts once, however long it stays in view.`;
		series = index.uniqueSeries;
	}

	// how many of each kind: counted so far, or in view now (Most in view)
	const byKind = kinds.length
		? countByKind(
				mode === 'max'
					? Array.from(
							{ length: index.perFrame[f + 1] - index.perFrame[f] },
							(_, i) => index.tracks[index.trackOf[index.perFrame[f] + i]]
						)
					: index.tracks.filter(tr => (tr.confirmed_at ?? tr.t0) <= f),
				kinds
			)
		: [];

	if (filtered && !noteWarn)
		note = `Counted at sensitivity ${Math.round(deferred * 100)} % in the browser (the analysis used ${Math.round(floor * 100)} %)`;
	else if (sizeHidden > 0 && !noteWarn)
		note = `Counted without ${sizeHidden} of a size outside the range kept`;

	return {
		index,
		method,
		sensitivity: {
			value: sensitivity,
			min: floor,
			available: hasConfidence(tracksDoc),
			onChange: setSensitivity,
		},
		mode,
		setMode,
		changes,
		line,
		setLine,
		officialLine,
		lineMoved,
		frame: f,
		setFrame,
		count,
		total,
		// every counted one, whatever the mode (checking the count asks about these)
		uniqueTotal: index.uniqueSeries[last],
		events,
		subtitle,
		note,
		noteWarn,
		explain,
		series,
		hist,
		kinds,
		byKind,
		// the filter chain for the report's steps: every track, the sensitivity applied, and after sensitivity and the
		// corrections (index is after the size filter too)
		stages: { doc: tracksDoc, sensitivity: deferred, base: baseIndex },
		sizeFilter: {
			unit: baseSizes?.unit || 'px',
			across: !!baseSizes?.across,
			groups: sizeGroups,
			// the slider opened to every size is no filter
			setRange: (g, r) =>
				setChosen(c => {
					const { [g]: _, ...rest } = c;
					return r == null ? rest : { ...c, [g]: r };
				}),
			reset: g =>
				setChosen(c => {
					const { [g]: _, ...rest } = c;
					return rest;
				}),
		},
	};
};

export default useCountModel;
